<script setup>
import { FlexRender } from '@tanstack/vue-table';
import SortButton from './SortButton.vue';
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  table: {
    type: Object,
    required: true,
  },
  type: {
    type: String,
    default: 'relaxed',
  },
});

const { t } = useI18n();

const MIN_COLUMN_WIDTH = 40;
const tableRef = ref(null);
const columnWidths = ref({});
let stopResize = null;

const isRelaxed = computed(() => props.type === 'relaxed');
const isSummary = computed(() => props.type === 'summary');
const rows = computed(() => props.table.getRowModel().rows);
const headers = computed(() => props.table.getHeaderGroups()[0].headers);

const headerTitle = header => header.column.columnDef.meta?.headerTitle;

const summaryHeaderLayout = computed(() => {
  if (!isSummary.value) {
    return { topRow: null, bottomHeaders: headers.value };
  }

  const topRow = [];
  const bottomHeaders = [];
  let index = 0;

  while (index < headers.value.length) {
    const header = headers.value[index];
    const meta = header.column.columnDef.meta;

    if (meta?.groupKey && meta?.groupLabel) {
      const groupKey = meta.groupKey;
      let end = index + 1;
      while (
        end < headers.value.length &&
        headers.value[end].column.columnDef.meta?.groupKey === groupKey
      ) {
        end += 1;
      }

      topRow.push({
        type: 'group',
        label: meta.groupLabel,
        colspan: end - index,
      });
      bottomHeaders.push(...headers.value.slice(index, end));
      index = end;
    } else {
      topRow.push({
        type: 'single',
        header,
        colspan: 1,
        rowspan: 2,
      });
      index += 1;
    }
  }

  return {
    topRow: topRow.some(cell => cell.type === 'group') ? topRow : null,
    bottomHeaders: topRow.some(cell => cell.type === 'group')
      ? bottomHeaders
      : headers.value,
  };
});

const defaultColumnWidth = header => {
  const meta = header.column.columnDef.meta;
  if (!meta?.primary) return undefined;
  return meta.primaryWide ? 192 : 52;
};

const columnStyle = header => {
  if (!isSummary.value) return undefined;
  const width =
    columnWidths.value[header.column.id] ?? defaultColumnWidth(header);
  return width ? { width: `${width}px` } : undefined;
};

const tableStyle = computed(() => {
  const widths = headers.value.map(h => columnWidths.value[h.column.id]);
  if (!isSummary.value || widths.some(width => !width)) return undefined;
  return { width: `${widths.reduce((sum, width) => sum + width, 0)}px` };
});

const resetColumnWidths = () => {
  columnWidths.value = {};
};

const startResize = (event, header) => {
  const measured = {};
  tableRef.value.querySelectorAll('th[data-column-id]').forEach(th => {
    measured[th.dataset.columnId] = Math.round(
      th.getBoundingClientRect().width
    );
  });
  columnWidths.value = measured;

  const columnId = header.column.id;
  const startX = event.clientX;
  const startWidth = measured[columnId];

  const onMove = moveEvent => {
    columnWidths.value = {
      ...columnWidths.value,
      [columnId]: Math.max(
        MIN_COLUMN_WIDTH,
        startWidth + moveEvent.clientX - startX
      ),
    };
  };
  // Swallow the click that follows the drag so it doesn't toggle sorting on the <th>
  const onUp = () => {
    stopResize();
    window.addEventListener('click', e => e.stopPropagation(), {
      capture: true,
      once: true,
    });
  };
  stopResize = () => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', stopResize);
    stopResize = null;
  };
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', stopResize);
};

onBeforeUnmount(() => stopResize?.());

const thClass = (header, rowSpan = 1) => {
  if (!isSummary.value) {
    return 'py-2.5 px-4 text-xs font-medium text-n-slate-11 uppercase tracking-wider';
  }

  if (header.column.columnDef.meta?.primary) {
    const wide = header.column.columnDef.meta?.primaryWide;
    return [
      wide
        ? 'py-1.5 px-1 text-[10px] font-medium text-n-slate-11 text-center align-bottom'
        : 'py-1.5 px-0.5 text-[10px] font-medium text-n-slate-11 text-center align-bottom',
      rowSpan > 1 ? 'align-middle' : '',
    ];
  }

  return 'py-1.5 px-0.5 text-[10px] leading-[1.15] font-medium text-n-slate-11 text-center align-bottom';
};

const groupThClass =
  'py-1.5 px-0.5 text-[10px] leading-[1.15] font-medium text-n-slate-11 text-center align-bottom border-b border-n-weak';

const tdClass = (cell, rowIndex) => {
  const base = [
    'border-b border-n-weak text-n-slate-12',
    rowIndex === rows.value.length - 1 ? 'border-b-0' : '',
  ];

  if (isSummary.value) {
    if (cell.column.columnDef.meta?.primary) {
      base.push(
        cell.column.columnDef.meta?.primaryWide
          ? 'py-1 px-1 overflow-hidden align-top'
          : 'py-1 px-0.5 overflow-hidden align-top'
      );
    } else {
      base.push(
        'py-1 px-0.5 text-[10px] leading-tight text-center tabular-nums align-top overflow-hidden'
      );
    }
    return base;
  }

  base.push(
    isRelaxed.value ? 'py-3.5 px-4 text-sm' : 'py-2 px-4 text-sm truncate'
  );
  return base;
};
</script>

<template>
  <div :class="isSummary ? 'overflow-x-auto' : ''">
    <table
      ref="tableRef"
      class="w-full border-collapse table-fixed rounded-xl overflow-hidden"
      :style="tableStyle"
    >
      <colgroup v-if="isSummary">
        <col
          v-for="header in headers"
          :key="header.id"
          :style="columnStyle(header)"
        />
      </colgroup>
      <thead>
        <tr v-if="summaryHeaderLayout.topRow">
          <template
            v-for="(cell, cellIndex) in summaryHeaderLayout.topRow"
            :key="`top-${cellIndex}`"
          >
            <th
              v-if="cell.type === 'group'"
              :colspan="cell.colspan"
              :class="groupThClass"
            >
              {{ cell.label }}
            </th>
            <th
              v-else
              :rowspan="cell.rowspan"
              class="relative text-left bg-n-slate-3 border-b border-n-weak first:rounded-tl-xl cursor-pointer select-none"
              :class="thClass(cell.header, cell.rowspan)"
              :data-column-id="cell.header.column.id"
              :title="headerTitle(cell.header)"
              @click="
                cell.header.column.getCanSort() &&
                cell.header.column.toggleSorting()
              "
            >
              <div
                v-if="!cell.header.isPlaceholder"
                class="flex gap-0.5 items-center justify-center"
              >
                <FlexRender
                  :render="cell.header.column.columnDef.header"
                  :props="cell.header.getContext()"
                />
              </div>
              <div
                v-if="isSummary"
                class="absolute top-0 right-0 z-10 w-1.5 h-full cursor-col-resize select-none touch-none hover:bg-n-brand/40"
                :title="t('SUMMARY_REPORTS.RESIZE_COLUMN_HINT')"
                @pointerdown.stop.prevent="startResize($event, cell.header)"
                @click.stop
                @dblclick.stop="resetColumnWidths"
              />
            </th>
          </template>
        </tr>
        <tr v-if="summaryHeaderLayout.bottomHeaders.length">
          <th
            v-for="header in summaryHeaderLayout.bottomHeaders"
            :key="header.id"
            class="relative text-left bg-n-slate-3 border-b border-n-weak last:rounded-tr-xl cursor-pointer select-none"
            :data-column-id="header.column.id"
            :class="[
              thClass(header),
              summaryHeaderLayout.topRow ? '' : 'first:rounded-tl-xl',
            ]"
            :title="headerTitle(header)"
            @click="header.column.getCanSort() && header.column.toggleSorting()"
          >
            <div
              v-if="!header.isPlaceholder"
              class="flex gap-0.5"
              :class="
                isSummary && header.column.columnDef.meta?.primary
                  ? 'items-center justify-center'
                  : isSummary
                    ? 'items-end justify-center whitespace-normal break-words hyphens-auto'
                    : 'items-center truncate'
              "
            >
              <span
                :class="
                  isSummary && !header.column.columnDef.meta?.primary
                    ? ''
                    : 'truncate'
                "
              >
                <FlexRender
                  :render="header.column.columnDef.header"
                  :props="header.getContext()"
                />
              </span>
              <SortButton v-if="header.column.getCanSort()" :header="header" />
            </div>
            <div
              v-if="isSummary"
              class="absolute top-0 right-0 z-10 w-1.5 h-full cursor-col-resize select-none touch-none hover:bg-n-brand/40"
              :title="t('SUMMARY_REPORTS.RESIZE_COLUMN_HINT')"
              @pointerdown.stop.prevent="startResize($event, header)"
              @click.stop
              @dblclick.stop="resetColumnWidths"
            />
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="(row, rowIndex) in rows"
          :key="row.id"
          class="hover:bg-n-alpha-1 transition-colors duration-100"
        >
          <td
            v-for="cell in row.getVisibleCells()"
            :key="cell.id"
            :class="tdClass(cell, rowIndex)"
          >
            <FlexRender
              :render="cell.column.columnDef.cell"
              :props="cell.getContext()"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
